import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { PageLoader } from '@/components/feedback/PageLoader';
import {
  useAuthStore,
  useInactivityTimeout,
  useSessionExpiryTimer,
  redirectToAuthServer,
} from '@/features/auth';

/**
 * Guards every authenticated route. BFM Online has no login page of its own:
 * an unauthenticated visitor, or one whose session has expired, is sent
 * straight to the Barita auth server (which owns credentials, security
 * questions, and MFA) rather than to any local screen. `state` and the
 * originally requested path are round-tripped via `redirectToAuthServer` so
 * Barita returns the user to `/auth/callback`; a successful login then always
 * lands on the home route, regardless of what page originally triggered the
 * redirect.
 *
 * An already-authenticated, not-yet-expired visit renders `<Outlet />`
 * directly — no auth-server round trip on every request.
 */
export function ProtectedRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isSessionExpired = useAuthStore((state) => state.isSessionExpired());
  const logout = useAuthStore((state) => state.logout);
  const location = useLocation();
  const needsAuth = !isAuthenticated || isSessionExpired;

  // BR-14: clears the session after 30 idle minutes. Combined with the
  // absolute-expiry timer below, either one flips `needsAuth` to true, which
  // the effect below turns into a fresh Barita redirect.
  useInactivityTimeout();
  useSessionExpiryTimer();

  useEffect(() => {
    if (!needsAuth) return;
    // Idempotent when there was never a session to begin with; guarantees a
    // clean slate before handing off to Barita either way. Deliberately not
    // gated on `isSessionExpired` itself: that value flips to `false` the
    // moment `logout()` clears `expiresAt`, and depending on it here would
    // re-fire this effect a second time (still `needsAuth`, now for the
    // "never authenticated" reason) and double up the redirect.
    logout();
    redirectToAuthServer(location.pathname + location.search);
    // A real navigation away from the SPA follows; nothing else to do here.
  }, [needsAuth, logout, location.pathname, location.search]);

  // Render a loader (never the protected content) while the redirect above
  // takes effect, so a guarded page is never flashed to a signed-out visitor.
  if (needsAuth) return <PageLoader />;

  return <Outlet />;
}
